import {
  PROPERTY_CALC_KEYS,
  baseDataCompleteness,
  formatAmountInput,
  parseAmountInput,
  type BaseDataCompleteness,
  type PropertyState,
} from '@brimo/shared';
import { formatNumber, parseDecimal, parseInteger } from '../format';
import type { PatchPropertyBody, PropertyRecord } from '../types';
import { calcDefaultsBody, type CalcDefaultsDraft } from './calcDefaultsDraft';
import { DOCUMENTS_URL_MESSAGE, documentsUrlValue } from './documentsFolder';

/**
 * Draft of the base-data sheet („Stammdaten", Nico feedback FB-004, D-315): the text of every field, the PATCH
 * body built from it and the live completeness (`baseDataCompleteness`, the same rule as the server's). The group
 * „Kalkulation" (D-362) edits the eight „Objekt-Vorgaben" through the shared `CalcDefaultsDraft`.
 */

export interface BaseDataDraft {
  yearBuiltFrom: string;
  yearBuiltUntil: string;
  yearRenovated: string;
  livingAreaTotalSqm: string;
  energyCertificate: string;
  heating: string;
  readyForOccupancy: string;
  parkingSpacesCount: string;
  parkingSpacePriceCents: string;
  serviceChargePerSqmCents: string;
  notary: string;
  declarationOfDivisionOn: string;
  coOwnershipSharesTotal: string;
  /** '' = automatisch nach PLZ */
  state: PropertyState | '';
  landTransferTaxPct: string;
  managerUserId: string;
  documentsUrl: string;
}

const numberText = (value: number | null, decimals: number) =>
  value === null ? '' : formatNumber(value, decimals);
const integerText = (value: number | null) => (value === null ? '' : String(value));
const amountText = (value: number | null) => (value === null ? '' : formatAmountInput(value));

export function baseDataDraftOf(record: PropertyRecord): BaseDataDraft {
  const f = record.fields;
  return {
    yearBuiltFrom: integerText(f.yearBuiltFrom),
    yearBuiltUntil: integerText(f.yearBuiltUntil),
    yearRenovated: integerText(f.yearRenovated),
    livingAreaTotalSqm: numberText(f.livingAreaTotalSqm, 1),
    energyCertificate: f.energyCertificate ?? '',
    heating: f.heating ?? '',
    readyForOccupancy: f.readyForOccupancy ?? '',
    parkingSpacesCount: integerText(f.parkingSpacesCount),
    parkingSpacePriceCents: amountText(f.parkingSpacePriceCents),
    serviceChargePerSqmCents: amountText(f.serviceChargePerSqmCents),
    notary: f.notary ?? '',
    declarationOfDivisionOn: f.declarationOfDivisionOn ?? '',
    coOwnershipSharesTotal: numberText(f.coOwnershipSharesTotal, 4),
    state: f.stateSource === 'manual' && f.state !== null ? f.state : '',
    landTransferTaxPct: numberText(f.landTransferTaxPct, 2),
    managerUserId: f.managerUserId ?? '',
    documentsUrl: f.documentsUrl ?? '',
  };
}

const text = (value: string): string | null => (value.trim() === '' ? null : value.trim());

/** The state the property ends up with: the chosen one, else the automatic one (unknown after clearing a manual choice). */
function effectiveState(record: PropertyRecord, draft: BaseDataDraft): PropertyState | null {
  if (draft.state !== '') return draft.state;
  return record.fields.stateSource === 'postal_code' ? record.fields.state : null;
}

/**
 * The PATCH body of the sheet and the field errors of unreadable numbers. Locked fields are left out by the caller
 * (`withoutManagedFields`); `state` is only sent when the choice changed (as in „Objekt bearbeiten").
 */
export function baseDataBody(
  record: PropertyRecord,
  draft: BaseDataDraft,
  isLocked: (field: string) => boolean,
): { body: PatchPropertyBody; errors: Record<string, string> } {
  const errors: Record<string, string> = {};
  const numeric = (key: keyof BaseDataDraft, parse: (value: string) => number | null): number | null => {
    const value = draft[key];
    if (value.trim() === '') return null;
    const parsed = parse(value);
    if (parsed === null && !isLocked(key)) errors[key] = 'Bitte eine Zahl angeben.';
    return parsed;
  };
  const body: PatchPropertyBody = {
    yearBuiltFrom: numeric('yearBuiltFrom', parseInteger),
    yearBuiltUntil: numeric('yearBuiltUntil', parseInteger),
    yearRenovated: numeric('yearRenovated', parseInteger),
    livingAreaTotalSqm: numeric('livingAreaTotalSqm', parseDecimal),
    energyCertificate: text(draft.energyCertificate),
    heating: text(draft.heating),
    readyForOccupancy: text(draft.readyForOccupancy),
    parkingSpacesCount: numeric('parkingSpacesCount', parseInteger),
    parkingSpacePriceCents: numeric('parkingSpacePriceCents', parseAmountInput),
    serviceChargePerSqmCents: numeric('serviceChargePerSqmCents', parseAmountInput),
    notary: text(draft.notary),
    declarationOfDivisionOn: text(draft.declarationOfDivisionOn),
    coOwnershipSharesTotal: numeric('coOwnershipSharesTotal', parseDecimal),
    landTransferTaxPct: numeric('landTransferTaxPct', parseDecimal),
    managerUserId: draft.managerUserId || null,
  };
  const documentsUrl = documentsUrlValue(draft.documentsUrl);
  if (!documentsUrl.valid) errors['documentsUrl'] = DOCUMENTS_URL_MESSAGE;
  body.documentsUrl = documentsUrl.url;
  if (draft.state !== baseDataDraftOf(record).state) body.state = draft.state === '' ? null : draft.state;
  return { body, errors };
}

/** Completeness of what the sheet would save (unreadable numbers count as missing). */
export function draftCompleteness(
  record: PropertyRecord,
  draft: BaseDataDraft,
  calcDraft: CalcDefaultsDraft,
): BaseDataCompleteness {
  const { body } = baseDataBody(record, draft, () => true);
  const calc = calcDefaultsBody(calcDraft, PROPERTY_CALC_KEYS).body;
  return baseDataCompleteness({
    yearBuiltFrom: body.yearBuiltFrom ?? null,
    livingAreaTotalSqm: body.livingAreaTotalSqm ?? null,
    energyCertificate: body.energyCertificate ?? null,
    heating: body.heating ?? null,
    serviceChargePerSqmCents: body.serviceChargePerSqmCents ?? null,
    parkingSpacesCount: body.parkingSpacesCount ?? null,
    readyForOccupancy: body.readyForOccupancy ?? null,
    notary: body.notary ?? null,
    managerUserId: body.managerUserId ?? null,
    state: effectiveState(record, draft),
    landTransferTaxPct: body.landTransferTaxPct ?? null,
    yearRenovated: body.yearRenovated ?? null,
    parkingSpacePriceCents: body.parkingSpacePriceCents ?? null,
    declarationOfDivisionOn: body.declarationOfDivisionOn ?? null,
    coOwnershipSharesTotal: body.coOwnershipSharesTotal ?? null,
    documentsUrl: body.documentsUrl ?? null,
    calcDepreciationPct: calc.depreciationPct ?? null,
    calcBuildingSharePct: calc.buildingSharePct ?? null,
    calcInterestPct: calc.interestPct ?? null,
    calcRepaymentPct: calc.repaymentPct ?? null,
    calcSevCents: calc.sevCents ?? null,
    calcWegCents: calc.wegCents ?? null,
    calcReservePerSqmCents: calc.reservePerSqmCents ?? null,
    calcOtherCostsPerSqmCents: calc.otherCostsPerSqmCents ?? null,
  });
}

/** „8 von 11 Pflichtangaben" */
export function progressText(completeness: Pick<BaseDataCompleteness, 'doneCount' | 'totalCount'>): string {
  return `${completeness.doneCount} von ${completeness.totalCount} Pflichtangaben`;
}
